import { useCallback, useMemo, useState } from 'react';
import BrowserView from './components/BrowserView';
import { getSiteTheme, getStoredDarkMode, persistDarkMode } from './styles/siteTheme';

const DOCS_URL = 'https://github.com/all-completed/main';

const PROJECTS = [
  {
    title: 'Remote Browser',
    url: 'https://rb.all-completed.com',
    hostname: 'rb.all-completed.com',
    description: 'The Remote Browser environment hosted under the All Completed domain.',
    previewText: 'Open the Remote Browser app in a new tab.'
  },
  {
    title: 'Tuning',
    url: 'https://tuning.all-completed.com',
    hostname: 'tuning.all-completed.com',
    description: 'The Tuning environment deployed under the All Completed domain.',
    previewText: 'Open the Tuning app in a new tab.'
  }
];

function LaunchIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" style={{ width: 16, height: 16 }}>
      <path d="M12.232 4.232a.75.75 0 0 1 1.06 0l2.475 2.475a.75.75 0 0 1 0 1.06l-7.5 7.5a.75.75 0 0 1-.386.207l-3.25.65a.75.75 0 0 1-.88-.88l.65-3.25a.75.75 0 0 1 .207-.386l7.5-7.5Z" />
      <path d="M13.06 3.172a2.25 2.25 0 0 1 3.182 0l.586.586a2.25 2.25 0 0 1 0 3.182l-.586.586-3.768-3.768.586-.586Z" />
    </svg>
  );
}

function App() {
  const [darkMode, setDarkMode] = useState(getStoredDarkMode);
  const theme = useMemo(() => getSiteTheme(darkMode), [darkMode]);

  const toggleTheme = useCallback(() => {
    setDarkMode((prev) => {
      const next = !prev;
      persistDarkMode(next);
      return next;
    });
  }, []);

  const styles = useMemo(() => {
    const container = {
      width: '100%',
      maxWidth: 1120,
      margin: '0 auto',
      padding: '0 16px'
    };

    const buttonBase = {
      appearance: 'none',
      border: `1px solid ${theme.border2}`,
      borderRadius: 12,
      padding: '10px 14px',
      fontSize: 14,
      fontWeight: 600,
      lineHeight: 1,
      color: theme.text,
      background: 'transparent',
      cursor: 'pointer',
      textDecoration: 'none',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 8,
      transition: 'transform 120ms ease, background 120ms ease, border-color 120ms ease'
    };

    return {
      page: {
        minHeight: '100vh',
        background: theme.bg,
        color: theme.text,
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflowX: 'hidden'
      },
      glow: {
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        background: theme.glowBg
      },
      nav: {
        position: 'sticky',
        top: 0,
        zIndex: 50,
        borderBottom: `1px solid ${theme.border}`,
        background: theme.navBg,
        backdropFilter: 'blur(14px)'
      },
      navInner: {
        ...container,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        gap: 12,
        paddingTop: 14,
        paddingBottom: 14,
        minHeight: 52
      },
      brand: {
        display: 'flex',
        alignItems: 'baseline',
        gap: 10,
        color: theme.text,
        cursor: 'pointer',
        userSelect: 'none'
      },
      brandTitle: {
        fontSize: 16,
        fontWeight: 800,
        letterSpacing: '-0.02em'
      },
      brandBadge: {
        fontSize: 12,
        color: theme.muted,
        padding: '4px 10px',
        borderRadius: 999,
        border: `1px solid ${theme.border}`,
        background: theme.brandBadgeBg
      },
      btnGhost: {
        ...buttonBase,
        background: theme.btnGhostBg
      },
      btnPrimary: {
        ...buttonBase,
        borderColor: darkMode ? 'rgba(96, 165, 250, 0.35)' : 'rgba(59, 130, 246, 0.4)',
        background: theme.btnPrimaryBg,
        color: theme.btnPrimaryColor
      },
      main: {
        flex: 1,
        width: '100%',
        padding: '64px 0 52px'
      },
      heroSection: {
        ...container,
        textAlign: 'center',
        paddingTop: 8,
        paddingBottom: 26
      },
      heroTitle: {
        margin: '18px 0 10px',
        fontSize: 'clamp(44px, 7vw, 76px)',
        fontWeight: 900,
        letterSpacing: '-0.04em',
        lineHeight: 1.02
      },
      heroSubtitle: {
        margin: '0 auto',
        maxWidth: 760,
        color: theme.muted,
        fontSize: 16,
        lineHeight: 1.6
      },
      heroActions: {
        marginTop: 22,
        display: 'flex',
        justifyContent: 'center',
        gap: 12,
        flexWrap: 'wrap'
      },
      projectsSection: {
        background: theme.bg
      },
      projectsInner: {
        ...container,
        paddingTop: 48,
        paddingBottom: 64
      },
      projectsGrid: {
        marginTop: 0,
        display: 'grid',
        gap: 18
      },
      projectCard: {
        background: theme.cardBg,
        border: `1px solid ${theme.border}`,
        borderRadius: 24,
        boxShadow: theme.shadow,
        padding: 22,
        display: 'grid',
        gap: 22,
        alignItems: 'center'
      },
      projectMeta: {
        margin: 0,
        fontSize: 12,
        fontWeight: 800,
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
        color: theme.muted2
      },
      projectTitle: {
        margin: '10px 0 0',
        fontSize: 28,
        fontWeight: 900,
        letterSpacing: '-0.03em'
      },
      projectText: {
        margin: '14px 0 0',
        color: theme.muted,
        fontSize: 15,
        lineHeight: 1.7
      },
      projectActions: {
        marginTop: 18,
        display: 'flex',
        gap: 10,
        flexWrap: 'wrap',
        alignItems: 'center'
      },
      footer: {
        borderTop: `1px solid ${theme.border}`,
        background: theme.footerBg,
        color: theme.muted,
        textAlign: 'center',
        fontSize: 14
      },
      footerInner: {
        ...container,
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 14,
        paddingTop: 22,
        paddingBottom: 22,
        color: theme.muted,
        fontSize: 14
      },
      footerLinks: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 16
      },
      footerLink: {
        color: theme.muted,
        textDecoration: 'none'
      }
    };
  }, [darkMode, theme]);

  return (
    <div style={styles.page}>
      <div style={styles.glow} />

      <nav style={styles.nav} aria-label="Primary">
        <div style={styles.navInner}>
          <div
            style={styles.brand}
            role="button"
            tabIndex={0}
            onClick={() => { window.location.href = '/'; }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') window.location.href = '/';
            }}
          >
            <div style={styles.brandTitle}>All Completed</div>
            <button
              type="button"
              style={{
                ...styles.brandBadge,
                cursor: 'pointer',
                margin: 0,
                font: 'inherit'
              }}
              onClick={(e) => {
                e.stopPropagation();
                toggleTheme();
              }}
              aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
              title={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {darkMode ? 'Dark' : 'Light'}
            </button>
          </div>
        </div>
      </nav>

      <main style={styles.main}>
        <section style={styles.heroSection}>
          <h1 style={styles.heroTitle}>All Completed</h1>
          <p style={styles.heroSubtitle}>
            A single landing page for jumping into our deployed apps. Pick a project below and open it directly in a new tab.
          </p>
          <div style={styles.heroActions}>
            <a style={styles.btnPrimary} href="#projects">
              Browse projects
            </a>
            <a
              style={{ ...styles.btnGhost, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none' }}
              href={DOCS_URL}
              target="_blank"
              rel="noreferrer"
            >
              Read docs
            </a>
          </div>
        </section>

        <section id="projects" style={styles.projectsSection}>
          <div style={styles.projectsInner}>
            <div style={styles.projectsGrid}>
              {PROJECTS.map((project, index) => {
                const previewAccent = index === 0 ? theme.projectAccentA : theme.projectAccentB;
                const isReversed = index % 2 === 1;

                return (
                  <article
                    key={project.title}
                    style={{
                      ...styles.projectCard,
                      gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))'
                    }}
                  >
                    <div style={{ order: isReversed ? 2 : 1 }}>
                      <p style={styles.projectMeta}>Project</p>
                      <h3 style={styles.projectTitle}>{project.title}</h3>
                      <p style={styles.projectText}>{project.description}</p>

                      <div style={styles.projectActions}>
                        <a style={styles.btnPrimary} href={project.url} target="_blank" rel="noreferrer">
                          Open {project.hostname}
                          <LaunchIcon />
                        </a>
                      </div>
                    </div>

                    <div style={{ order: isReversed ? 1 : 2 }}>
                      <BrowserView
                        theme={theme}
                        hostname={project.hostname}
                        title={project.title}
                        subtitle={project.previewText}
                        accent={previewAccent}
                      />
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
        </section>
      </main>

      <footer style={styles.footer}>
        <div style={styles.footerInner}>
          <div>© {new Date().getFullYear()} All Completed, LLC</div>
          <div style={styles.footerLinks}>
            <a style={styles.footerLink} href="https://rb.all-completed.com" target="_blank" rel="noreferrer">Remote Browser</a>
            <a style={styles.footerLink} href="https://tuning.all-completed.com" target="_blank" rel="noreferrer">Tuning</a>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
